import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EMAIL_LINK_TYPES, type EmailLinkProof, type EmailLinkType } from '@libs/dtos/auth/auth.dto';
import { AuthService } from '@services/auth/auth.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { AuthFormComponent } from '@shared/components/auth-form/auth-form.component';

/**
 * Activation of an invited account (10/09).
 *
 * Every invitation — backoffice user, portal member, provisioned candidate or
 * company — lands here. The link carries `?token_hash=…&type=…` (24/09), which
 * this screen does NOT spend on opening: it goes to the backend with the
 * password, when the form is sent. Corporate mail scanners open every link as
 * the email arrives, and the old link (Supabase's `/verify`, which bounced here
 * with a `#access_token`) was spent by that first visit. Links sent before
 * still arrive with the fragment and keep working. The person chooses a
 * password and the session opens right away; until this step the account
 * cannot log in.
 *
 * It is NOT the password reset, although both ask for the same two fields:
 * reset ends on a "go to login" card, because the person already had a
 * password; here they never had one, so asking them to type it again ten
 * seconds later was the whole complaint.
 *
 * Two ways to arrive without a token, both meaning "the link expired":
 * Supabase bounces an expired link back here with `#error_code=otp_expired`,
 * and the email itself carries `?resend=<email>` for the "request a new one"
 * line. Either shows the resend form instead of a dead end at the login —
 * an invited person has no password yet, so the login can do nothing for them.
 */
@Component({
	selector: 'app-activate-account',
	imports: [AuthFormComponent],
	templateUrl: './activate-account.component.html',
	styleUrl: './activate-account.component.scss',
})
export class ActivateAccountComponent implements OnInit {
	loading = false;
	/** What proves the link: its token_hash, or the session of an older link. */
	link: EmailLinkProof | null = null;
	/** `activate` = choose the password; `expired` = ask for a fresh link. */
	mode: 'activate' | 'expired' = 'activate';
	/** Address carried by the email's "request a new one" link, to prefill. */
	initialEmail: string | null = null;
	resendSent = false;
	/**
	 * A migrated candidate accepts the privacy policy before choosing a password.
	 * Only known once the link is spent: the backend answers `privacy_required`
	 * with a session to retry with, and the box is drawn then.
	 */
	requirePrivacy = false;

	constructor(
		private messageAlertService: MessageAlertService,
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
		private translocoService: TranslocoService,
	) {}

	ngOnInit(): void {
		const query = this.route.snapshot.queryParamMap;
		this.initialEmail = query.get('resend');

		const tokenHash = query.get('token_hash');
		const type = query.get('type') as EmailLinkType | null;
		if (tokenHash && type && EMAIL_LINK_TYPES.includes(type)) {
			this.link = { tokenHash, type };
			this.mode = 'activate';
			return;
		}

		this.route.fragment.subscribe((fragment) => {
			const params = new URLSearchParams(fragment ?? '');
			const accessToken = params.get('access_token');
			if (accessToken) {
				this.link = { accessToken };
				this.mode = 'activate';
				return;
			}

			if (params.get('error_code') === 'otp_expired' || this.initialEmail !== null) {
				this.mode = 'expired';
				return;
			}

			// No token and no sign of an expired link: nothing to activate with.
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('auth.activateAccount.messages.invalidLink'),
			);
			setTimeout(() => {
				this.router.navigate(['/login']);
			}, 3000);
		});
	}

	onSubmit(formData: {
		email?: string;
		newPassword?: string;
		confirmPassword?: string;
		privacyAccepted?: boolean;
	}): void {
		if (this.loading) {
			return;
		}
		if (this.mode === 'expired') {
			this.resend(formData.email);
			return;
		}
		if (!this.link || !formData.newPassword) {
			return;
		}

		this.loading = true;
		// On success the service adopts the session and navigates to the home
		// of the role; nothing else to do here.
		this.authService.activateAccount(this.link, formData.newPassword, formData.privacyAccepted).subscribe({
			next: (response: { privacy_required?: boolean; activation_token?: string } | null) => {
				if (!response?.privacy_required) return;
				// The account needs the box: draw it and retry with the session the
				// link opened (its token_hash is spent). Nothing was written yet.
				this.loading = false;
				this.requirePrivacy = true;
				if (response.activation_token) this.link = { accessToken: response.activation_token };
			},
			error: () => {
				this.loading = false;
			},
		});
	}

	private resend(email?: string): void {
		if (!email) {
			return;
		}
		this.loading = true;
		this.authService.resendActivationEmail(email).subscribe({
			next: () => {
				this.loading = false;
				this.resendSent = true;
			},
			error: () => {
				this.loading = false;
			},
		});
	}
}
